import * as React from 'react';
import { ComplexValue } from 'app/types-shared';
import { BowtieCase, BowtieCaseOtherConnection, BowtieTooltipOptions, BowtieTooltipType, CaseRegistrationTemplate,
  DraftConnections } from '../types';
import { ConnectedCasesState, InspectionEvaluations } from '../types-store';
import { TooltipBuilder } from './diagram-tooltip/tooltip-builder';
import { BowtieConnections } from './bowtie-connections';

import styles from './bowtie-tooltip.scss';
import { ResizeObserver } from 'ui/slider-rating/ResizeObserver';
import { SEQUENCE_DIRECTION } from 'app/area/case/features/connected-cases/sequence';

interface BowtieTooltipPosition {
  left: number;
  top: number;
}

interface BowtieTooltipComponentState extends BowtieTooltipPosition {
  width: number;
}

interface BowtieTooltipViewProps extends BowtieTooltipPosition {
  refCallback: (elem: HTMLElement) => void;
  children?: React.ReactNode;
}

const BowtieTooltipView: React.FC<BowtieTooltipViewProps> = ({ left, top, refCallback, children }) => {
  const inlineStyle: React.CSSProperties = {
    left: Math.round(left),
    top: Math.round(top)
  };

  return <div style={inlineStyle} className={styles.bowtieTooltipBg} ref={refCallback}>
    {children}
  </div>;
};

interface BowtieTooltipComponentProps extends BowtieTooltipPosition {

  draftId: number;
  draftData: BowtieCase;
  caseData: BowtieCase;
  inspectionEvaluations: InspectionEvaluations;
  connectedCases: ConnectedCasesState;
  loadConnectedCases: (caseNumber: number, connectionType: number) => void;
  otherConnections: BowtieCaseOtherConnection[];
  draftConnections: DraftConnections[];
  connectionTypes: ComplexValue<number>[];
  tooltipOptions: BowtieTooltipOptions;
  connectCase: (caseNumber: number, connectionType: number) => void;
  disconnectCase: (caseNumber: number, connectionType: number) => void;
  componentElement: HTMLElement;
  caseTemplate: CaseRegistrationTemplate;
  translation: any;
  siblingCaseIds: number[];
  right: number;
  type: BowtieTooltipType;
  changeSequence?: (currentIndex: number, direction: SEQUENCE_DIRECTION) => void;
  parentCaseId?: number;
  sequenceOrder: number[];
  bowtieOverlayRef?: React.RefObject<HTMLDivElement>;
  changeConnectionSequenceId?: number;
  stateForCaseIds: { [caseId: string]: { valid: boolean; fetching: boolean } };
}

const getRect = (elem: HTMLElement) => {
  const rect = elem.getBoundingClientRect();
  return ({
    left: rect.left,
    top: rect.top + window.scrollY,
    width: rect.width || rect.right - rect.left,
    height: rect.height || rect.bottom - rect.top
  });
};

class BowtieTooltipContainer extends React.Component<BowtieTooltipComponentProps, BowtieTooltipComponentState> {
  public state: Readonly<BowtieTooltipComponentState> = {
    left: 0,
    top: 0,
    width: undefined
  };

  private tooltipElem: HTMLElement;

  private getBound = () => {

    if (!this.tooltipElem || !this.props.componentElement) {
      this.setState({
        left: 0,
        top: 0,
        width: undefined
      });
      return;
    }
    const parentRect = getRect(this.props.componentElement);
    const tooltipRect = getRect(this.tooltipElem);

    const left = this.props.left + tooltipRect.width > parentRect.width
      ? (this.props.right > tooltipRect.width
        ? this.props.right - tooltipRect.width
        : parentRect.width - tooltipRect.width)
      : this.props.left;

    const top = this.props.top + tooltipRect.height > parentRect.height
      ? parentRect.height - tooltipRect.height
      : this.props.top;

    this.setState({
      left: left,
      top: top,
      width: tooltipRect.width
    });
  };

  private setRef = (elem: HTMLElement) => {
    this.tooltipElem = elem;
    this.getBound();
  };

  public render() {
    const { caseData, caseTemplate, otherConnections,
      draftId, draftData, draftConnections,
      inspectionEvaluations, tooltipOptions,
      connectedCases, connectionTypes, loadConnectedCases,
      connectCase, disconnectCase, translation, type, siblingCaseIds, parentCaseId, changeSequence, sequenceOrder,
      bowtieOverlayRef, changeConnectionSequenceId, stateForCaseIds } = this.props;

    if (!caseData ||
      (type === BowtieTooltipType.connections && (!connectionTypes || connectionTypes.length === 0))) {
      return null;
    }

    return <BowtieTooltipView {...this.state} refCallback={this.setRef}>
      <ResizeObserver onResize={this.getBound}>
        {
          type === BowtieTooltipType.caseData && 
            <TooltipBuilder
              caseData={caseData}
              draftData={draftData}
              otherConnections={otherConnections}
              draftConnections={draftConnections}
              connectedCases={connectedCases}
              loadConnectedCases={loadConnectedCases}
              inspectionEvaluations={inspectionEvaluations}
              caseTemplate={caseTemplate}
              tooltipOptions={tooltipOptions}
              translation={translation} 
              siblingCaseIds={siblingCaseIds}
              parentCaseId={parentCaseId}
              changeSequence={changeSequence}
              sequenceOrder={sequenceOrder}
              bowtieOverlayRef={bowtieOverlayRef}
              changeConnectionSequenceId={changeConnectionSequenceId}
              stateForCaseIds={stateForCaseIds}
            />
        }
        {type === BowtieTooltipType.connections && <BowtieConnections
          draftId={draftId}
          caseData={caseData}
          draftConnections={draftConnections}
          disconnectCase={disconnectCase}
          connectionTypes={connectionTypes}
          connectCase={connectCase}
          translation={translation} />}
      </ResizeObserver>
    </BowtieTooltipView>;
  }
}

const BowtieTooltip = BowtieTooltipContainer;

export { BowtieTooltip };
